import { useEffect, useSyncExternalStore } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@cuspar/ui';
import { clarityEnabled, sendClarityConsent, startClarity } from '../../analytics/clarity';
import { usePreferencesStore } from '../../state/preferencesStore';
import { ROUTES } from '../../routes';

const noopSubscribe = () => () => {};

/**
 * Starts web analytics and asks the visitor whether analytics cookies are
 * allowed. The answer is kept with the other per-device preferences; "Cookie
 * settings" in the footer clears it, which shows the banner again.
 */
export const AnalyticsConsentBanner = () => {
  const { t } = useTranslation();
  const consent = usePreferencesStore((s) => s.analyticsConsent);
  const setConsent = usePreferencesStore((s) => s.setAnalyticsConsent);
  // The stored answer is only known in the browser; rendering the banner
  // during prerender/hydration would not match for returning visitors.
  const hydrated = useSyncExternalStore(
    noopSubscribe,
    () => true,
    () => false,
  );

  useEffect(() => {
    startClarity();
    sendClarityConsent(consent);
  }, [consent]);

  if (!clarityEnabled || !hydrated || consent !== null) return null;

  return (
    <div role='dialog' aria-label={t('consent.title')} data-testid='analytics-consent' className='fixed bottom-0 inset-x-0 z-50 border-t bg-background p-4 shadow-lg'>
      <div className='container flex flex-col gap-3 md:flex-row md:items-center md:justify-between'>
        <p className='text-sm text-muted-foreground'>
          {t('consent.body')}{' '}
          <Link to={ROUTES.legal.privacyPolicy} className='underline underline-offset-2 hover:text-foreground'>
            {t('nav.privacyPolicy')}
          </Link>
        </p>
        <div className='flex shrink-0 gap-2'>
          <Button variant='outline' size='sm' onClick={() => setConsent('denied')}>
            {t('consent.decline')}
          </Button>
          <Button size='sm' onClick={() => setConsent('granted')}>
            {t('consent.accept')}
          </Button>
        </div>
      </div>
    </div>
  );
};
